import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import PendingInviteBanner from '@/components/Organisation/PendingInviteBanner';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { usePermissions } from '@/hooks/usePermissions';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useOrgFeature } from '@/hooks/useOrgFeature';
import { useSubscription } from '@/hooks/useSubscription';
import {
  HomeIcon,
  BuildingOfficeIcon,
  FolderIcon,
  ChatBubbleLeftRightIcon,
  UserIcon,
  Cog6ToothIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UsersIcon,
  SpeakerWaveIcon,
  MicrophoneIcon,
  LanguageIcon,
  SpeakerXMarkIcon,
  ArrowPathIcon,
  FilmIcon,
  UserCircleIcon,
  MusicalNoteIcon,
  VideoCameraIcon,
  PhotoIcon,
  PuzzlePieceIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  ScissorsIcon,
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
  SparklesIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  NewspaperIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import ProjectSelector from '@/components/Common/ProjectSelector';
import OrganizationSwitcher from '@/components/Common/OrganizationSwitcher';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { hasPermission, canAccessModule } = usePermissions();
  const { isFeatureEnabled } = useFeatureFlags();
  const { isEnabled: isNewsroomEnabled } = useOrgFeature('newsroom');
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [lastVisitedByCategory, setLastVisitedByCategory] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem('lastVisitedSubmenu') || '{}');
    } catch {
      return {};
    }
  });
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const { data: subscription } = useSubscription();
  const hasTeamsFeature = subscription?.entitlements.approvalWorkflows === true;

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', String(next));
      return next;
    });
  };

  // Canva-style menu categories with permission filtering
  const menuCategories = [
    {
      id: 'home',
      name: 'Home',
      icon: HomeIcon,
      shortName: 'Home',
      items: [
        ...(hasPermission('dashboard', 'read') ? [{ name: 'Dashboard', href: '/app', icon: HomeIcon }] : []),
        ...(hasTeamsFeature ? [{ name: 'My Assignments', href: '/app/my-assignments', icon: ClipboardDocumentCheckIcon }] : []),
        ...(hasPermission('analytics', 'read') ? [{ name: 'Analytics', href: '/app/analytics', icon: ChartBarIcon }] : []),
      ]
    },
    {
      id: 'projects',
      name: 'Projects',
      icon: FolderIcon,
      shortName: 'Projects',
      items: [
        ...(hasPermission('projects', 'read') ? [
          { name: 'All Projects', href: '/app/projects', icon: FolderIcon },
          { name: 'Videos', href: '/app/projects/files/videos', icon: VideoCameraIcon },
          { name: 'Audio Files', href: '/app/projects/files/audio', icon: SpeakerWaveIcon },
          { name: 'Images', href: '/app/projects/files/images', icon: PhotoIcon },
        ] : []),
      ]
    },
    {
      id: 'audio',
      name: 'Audio',
      icon: SpeakerWaveIcon,
      shortName: 'Audio',
      items: [
        ...(hasPermission('text-to-speech', 'read') ? [
          { name: 'Text to Speech', href: '/app/audio/text-to-speech', icon: SpeakerWaveIcon },
        ] : []),
        ...(hasPermission('speech-to-text', 'read') ? [
          { name: 'Speech to Text', href: '/app/audio/speech-to-text', icon: MicrophoneIcon },
        ] : []),
        ...(hasPermission('audio-translation', 'read') ? [
          { name: 'Audio Translation', href: '/app/audio/translation', icon: LanguageIcon },
        ] : []),
        ...(hasPermission('text-translation', 'read') ? [
          { name: 'Text Translation', href: '/app/audio/text-translation', icon: LanguageIcon },
        ] : []),
        ...(hasPermission('voices', 'read') ? [
          { name: 'Voices', href: '/app/audio/voices', icon: MusicalNoteIcon },
        ] : []),
      ]
    },
    {
      id: 'video',
      name: 'Video',
      icon: VideoCameraIcon,
      shortName: 'Video',
      items: [
        ...(hasPermission('videos', 'read') ? [
          { name: 'Videos', href: '/app/videos', icon: FilmIcon },
        ] : []),
        ...(hasPermission('video-translation', 'read') ? [
          { name: 'Video Translation', href: '/app/audio/video-translation', icon: VideoCameraIcon },
        ] : []),
        ...(hasPermission('avatars', 'read') ? [
          { name: 'Avatars', href: '/app/audio/avatars', icon: UserCircleIcon },
        ] : []),
        ...(hasPermission('clip-generation', 'read') ? [
          { name: 'Clip Generator', href: '/app/clips', icon: ScissorsIcon },
          { name: 'Scheduled Posts', href: '/app/scheduled-posts', icon: CalendarDaysIcon },
        ] : []),
        ...(hasPermission('clip-generation', 'read') && isFeatureEnabled('video-editor') ? [
          { name: 'Video Editor', href: '/app/editor', icon: FilmIcon },
        ] : []),
        ...(hasPermission('video-understanding', 'read') && isFeatureEnabled('video-understanding') ? [
          { name: 'Video Understanding', href: '/app/video-understanding', icon: SparklesIcon },
        ] : []),
        ...(hasPermission('audio-to-video', 'read') ? [
          { name: 'Create Video', href: '/app/create-video', icon: FilmIcon },
        ] : []),
      ]
    },
    {
      id: 'newsroom',
      name: 'Newsroom',
      icon: NewspaperIcon,
      shortName: 'Newsroom',
      items: [
        ...(hasPermission('newsroom', 'read') && isNewsroomEnabled ? [
          { name: 'Articles', href: '/app/newsroom', icon: NewspaperIcon },
          { name: 'Sources', href: '/app/newsroom/sources', icon: AdjustmentsHorizontalIcon },
        ] : []),
      ]
    },
    {
      id: 'manage',
      name: 'Manage',
      icon: BuildingOfficeIcon,
      shortName: 'Manage',
      items: [
        ...(hasPermission('organizations', 'read') ? [{ name: 'Organization', href: '/app/organization', icon: BuildingOfficeIcon }] : []),
        ...(hasPermission('users', 'read') && hasTeamsFeature ? [{ name: 'Teams', href: '/app/teams', icon: UserGroupIcon }] : []),
        ...(hasPermission('roles', 'read') ? [{ name: 'Roles', href: '/app/roles', icon: ShieldCheckIcon }] : []),
        ...(hasPermission('integrations', 'read') ? [{ name: 'Integrations', href: '/app/integrations', icon: PuzzlePieceIcon }] : []),
        ...(hasPermission('configurations', 'read') ? [{ name: 'Configuration', href: '/app/configurations', icon: AdjustmentsHorizontalIcon }] : []),
        ...(hasPermission('settings', 'read') ? [{ name: 'Settings', href: '/app/settings', icon: Cog6ToothIcon }] : []),
        ...(hasPermission('settings', 'read') ? [{ name: 'Billing', href: '/app/settings/billing', icon: BanknotesIcon }] : []),
      ]
    },
  ].filter(category => category.items.length > 0);

  const superAdminCategory = isSuperAdmin ? {
    id: 'superadmin',
    name: 'Platform Admin',
    icon: ShieldCheckIcon,
    shortName: 'Admin',
    items: [
      { name: 'Platform Admin', href: '/app/superadmin', icon: ShieldCheckIcon },
    ]
  } : null;

  const allMenuCategories = superAdminCategory
    ? [...menuCategories, superAdminCategory]
    : menuCategories;

  // Highlight and default-open the category that matches the current route
  const activeCategory = useMemo(() => {
    let bestCategory: string | null = null;
    let bestLength = -1;
    for (const category of allMenuCategories) {
      for (const item of category.items) {
        if (location.pathname === item.href || location.pathname.startsWith(item.href + '/')) {
          if (item.href.length > bestLength) {
            bestLength = item.href.length;
            bestCategory = category.id;
          }
        }
      }
    }
    return bestCategory;
  }, [location.pathname, allMenuCategories]);

  // Remember the sub-menu item last visited within each main menu category
  useEffect(() => {
    if (!activeCategory) return;
    const category = allMenuCategories.find((c) => c.id === activeCategory);
    if (!category) return;

    let matchedHref: string | null = null;
    let bestLength = -1;
    for (const item of category.items) {
      if (location.pathname === item.href || location.pathname.startsWith(item.href + '/')) {
        if (item.href.length > bestLength) {
          bestLength = item.href.length;
          matchedHref = item.href;
        }
      }
    }
    if (!matchedHref || lastVisitedByCategory[activeCategory] === matchedHref) return;

    setLastVisitedByCategory((prev) => {
      const next = { ...prev, [activeCategory]: matchedHref as string };
      localStorage.setItem('lastVisitedSubmenu', JSON.stringify(next));
      return next;
    });
  }, [location.pathname, activeCategory, allMenuCategories, lastVisitedByCategory]);

  const visibleCategory = sidebarCollapsed ? null : (expandedCategory ?? activeCategory);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return SunIcon;
      case 'dark':
        return MoonIcon;
      default:
        return ComputerDesktopIcon;
    }
  };

  const ThemeIcon = getThemeIcon();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-xl">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Prime AI</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
            <OrganizationSwitcher />
          </div>
          <nav className="mt-8 px-4">
            {allMenuCategories.map((category) => (
              <div key={category.id} className="mb-6">
                <p className="px-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                  {category.name}
                </p>
                {category.items.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md mb-1 ${
                        isActive
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop Canva-style sidebar */}
      <div className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:z-50">
        {/* Icon Sidebar - Narrow */}
        <div className="w-[72px] bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col items-center py-4">
          {/* Logo */}
          <div className="mb-6">
            <div className="h-10 w-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
          </div>

          {/* Menu Categories */}
          <nav className="flex-1 flex flex-col items-center space-y-2 w-full px-2">
            {allMenuCategories.map((category) => {
              const isActive = activeCategory === category.id;
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => {
                    const target = lastVisitedByCategory[category.id] ?? category.items[0]?.href;
                    if (target) {
                      navigate(target);
                    }
                    if (sidebarCollapsed) {
                      setSidebarCollapsed(false);
                      localStorage.setItem('sidebarCollapsed', 'false');
                      setExpandedCategory(category.id);
                      return;
                    }
                    setExpandedCategory(expandedCategory === category.id ? null : category.id);
                  }}
                  className={`w-full flex flex-col items-center py-3 px-2 rounded-xl transition-all duration-200 group ${
                    isActive
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <Icon className="h-6 w-6 mb-1" />
                  <p className="text-xs font-medium text-gray-400 dark:text-gray-500 text-center mt-1 leading-tight">
                    {category.shortName}
                  </p>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Expanded Panel */}
        <div
          className={`w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300 ease-in-out overflow-hidden ${
            visibleCategory ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4 w-0 border-r-0'
          }`}
        >
          {visibleCategory && (
            <>
              {/* Panel Header */}
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-1.5">
                    {allMenuCategories.find((c) => c.id === visibleCategory)?.name}
                  </p>
                  <div className="space-y-2">
                    <OrganizationSwitcher />
                    <ProjectSelector />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={toggleSidebarCollapsed}
                  className="shrink-0 p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  title="Collapse sidebar"
                >
                  <ChevronDoubleLeftIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Panel Items */}
              <nav className="flex-1 overflow-y-auto p-3">
                {allMenuCategories
                  .find((c) => c.id === visibleCategory)
                  ?.items.map((item) => {
                    const isItemActive = location.pathname === item.href;
                    const ItemIcon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-3 py-2.5 text-base font-medium rounded-lg mb-1 transition-colors ${
                          isItemActive
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <ItemIcon className="mr-3 h-5 w-5 flex-shrink-0" />
                        <span className="truncate">{item.name}</span>
                      </Link>
                    );
                  })}
              </nav>
            </>
          )}
        </div>

        {/* Re-expand handle — shown only while collapsed, sits on the icon rail's edge */}
        {sidebarCollapsed && (
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            title="Expand sidebar"
            className="absolute top-1/2 left-[72px] -translate-y-1/2 -translate-x-1/2 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 shadow-md hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ChevronDoubleRightIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Main content */}
      <div className={`flex flex-col flex-1 transition-all duration-300 ${
        visibleCategory ? 'lg:pl-[336px]' : 'lg:pl-[72px]'
      }`}>
        {/* Desktop Header */}
        <header className="hidden lg:flex h-12 px-6 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Prime AI</h1>
          </div>
          <div className="flex items-center space-x-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Toggle Theme"
            >
              <ThemeIcon className="h-5 w-5" />
            </button>
            
            {/* Settings */}
            <Link
              to="/app/settings"
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Settings"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </Link>
            
            {/* Profile */}
            <Link
              to="/app/settings"
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Profile"
            >
              <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            </Link>
            
            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Logout"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </header>

        {/* Top bar for mobile */}
        <div className="lg:hidden">
          <div className="flex items-center justify-between h-16 px-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Prime AI</h1>
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <ThemeIcon className="h-5 w-5" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 px-6 pt-3 pb-6">
          <PendingInviteBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
